import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import {
  getParentChildren,
  getPublicPlans,
  getParentDiscountStatus,
  upgradeChildPlan,
  previewChildPlanChange,
  changeChildPlan,
  cancelChildPlan,
  resumeChildPlan,
  type ParentChild,
  type PublicPlan,
  type PlanChangePreview,
} from "../../../services/api";
import MobileAppLayout from "../../../layouts/MobileAppLayout";
import { GameCard, GameHero } from "../../../components/game";
import { expandedFeatures } from "../../../lib/planFeatures";

/**
 * Plans for the parent's children.
 *
 * Until this screen existed, a plan could only be chosen while CREATING a
 * child account — a child already on Free could never be moved to a paid plan
 * from inside the app. Each child here shows their current plan and, when they
 * are not already on an active paid one, a picker that hands off to Stripe.
 */
export default function AppParentPlans() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const status = params.get("status"); // "success" | "cancel"

  const [children, setChildren] = useState<ParentChild[]>([]);
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [paidCount, setPaidCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Per-child picker state, so a parent with several kids can set each one up
  // without the selections bleeding into each other.
  const [choice, setChoice] = useState<Record<string, { plan: string; billing: "month" | "year" }>>({});
  const [busyChild, setBusyChild] = useState<string | null>(null);

  // Proration confirmation for a child already on a paid plan: we show what
  // Stripe would charge (or credit) BEFORE touching the subscription, and carry
  // the preview's proration_date into the confirm so the amount can't drift.
  const [pendingChange, setPendingChange] = useState<
    { childId: string; childName: string; preview: PlanChangePreview } | null
  >(null);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState("");

  // Stopping a paid plan is a separate confirmation: it is not priced, it just
  // needs the parent to understand when the features actually stop.
  const [pendingCancel, setPendingCancel] = useState<
    { childId: string; childName: string; until: string | null } | null
  >(null);

  useEffect(() => {
    (async () => {
      try {
        const [kids, p, d] = await Promise.all([
          getParentChildren(),
          getPublicPlans(),
          getParentDiscountStatus().catch(() => ({ paid_count: 0 })),
        ]);
        setChildren(kids);
        setPlans(p);
        setPaidCount(d?.paid_count ?? 0);
        const firstPaid = p.find((x) => !x.is_free);
        if (firstPaid) {
          const seed: Record<string, { plan: string; billing: "month" | "year" }> = {};
          for (const k of kids) seed[k.athlete_id] = { plan: firstPaid.code, billing: "month" };
          setChoice(seed);
        }
      } catch {
        setError(t("plans.load_error", "Nu am putut încărca planurile."));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const paidPlans = useMemo(() => plans.filter((p) => !p.is_free), [plans]);
  const freePlan = useMemo(() => plans.find((p) => p.is_free) || null, [plans]);
  const planByCode = useMemo(() => new Map(plans.map((p) => [p.code, p])), [plans]);

  const money = (cents: number, currency: string) =>
    `${(cents / 100).toLocaleString("ro-RO", { minimumFractionDigits: cents % 100 ? 2 : 0 })} ${currency}`;

  /** Discount the parent reaches once one more paid plan is added. */
  const projectedPaid = paidCount + 1;
  const projectedDiscount = projectedPaid >= 3 ? 30 : projectedPaid === 2 ? 20 : 0;

  /** Child already on a paid plan → price the switch, then ask to confirm. */
  const handleChangePlan = async (childId: string, childName: string) => {
    const pick = choice[childId];
    if (!pick?.plan) {
      setError(t("plans.pick_plan", "Alege un plan."));
      return;
    }
    setBusyChild(childId);
    setError("");
    try {
      const preview = await previewChildPlanChange(childId, pick.plan, pick.billing);
      setPendingChange({ childId, childName, preview });
    } catch (e: any) {
      setError(e?.response?.data?.error || t("plans.preview_error", "Nu am putut calcula costul schimbării."));
    } finally {
      setBusyChild(null);
    }
  };

  const confirmChange = async () => {
    if (!pendingChange) return;
    const pick = choice[pendingChange.childId];
    setConfirming(true);
    setError("");
    try {
      const res = await changeChildPlan(
        pendingChange.childId,
        pick.plan,
        pick.billing,
        pendingChange.preview.proration_date
      );
      setPendingChange(null);
      if (res.payment_url) {
        // The proration invoice needs the parent (3DS, or a card retry).
        window.location.href = res.payment_url;
        return;
      }
      setNotice(t("plans.change_done", "Planul a fost schimbat."));
      setChildren(await getParentChildren());
    } catch (e: any) {
      setError(e?.response?.data?.error || t("plans.change_error", "Nu am putut schimba planul."));
      setPendingChange(null);
    } finally {
      setConfirming(false);
    }
  };

  const askCancel = (childId: string, childName: string, until: string | null) => {
    setError("");
    setPendingCancel({ childId, childName, until });
  };

  const confirmCancel = async () => {
    if (!pendingCancel) return;
    setConfirming(true);
    setError("");
    try {
      await cancelChildPlan(pendingCancel.childId);
      setPendingCancel(null);
      setNotice(t("plans.cancel_done", "Am oprit reînnoirea. Planul rămâne activ până la finalul perioadei plătite."));
      setChildren(await getParentChildren());
    } catch (e: any) {
      setError(e?.response?.data?.error || t("plans.cancel_error", "Nu am putut opri abonamentul."));
      setPendingCancel(null);
    } finally {
      setConfirming(false);
    }
  };

  const handleResume = async (childId: string) => {
    setBusyChild(childId);
    setError("");
    try {
      await resumeChildPlan(childId);
      setNotice(t("plans.resume_done", "Abonamentul continuă. Nu mai trece pe Free."));
      setChildren(await getParentChildren());
    } catch (e: any) {
      setError(e?.response?.data?.error || t("plans.resume_error", "Nu am putut reactiva abonamentul."));
    } finally {
      setBusyChild(null);
    }
  };

  const handleUpgrade = async (childId: string) => {
    const pick = choice[childId];
    if (!pick?.plan) {
      setError(t("plans.pick_plan", "Alege un plan."));
      return;
    }
    setBusyChild(childId);
    setError("");
    try {
      const url = await upgradeChildPlan(childId, pick.plan, pick.billing);
      if (url) {
        window.location.href = url; // hand off to Stripe Checkout
        return;
      }
      setError(t("plans.no_checkout", "Nu am putut iniția plata."));
      setBusyChild(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || t("plans.no_checkout", "Nu am putut iniția plata."));
      setBusyChild(null);
    }
  };

  return (
    <MobileAppLayout>
      <div className="pb-8">
        <GameHero flow className="rounded-br-3xl px-4 pt-3 pb-5">
          <div className="flex items-center gap-2.5">
            <Link to="/app/dashboard" className="-ml-1.5 p-1.5 rounded-full hover:bg-white/10 transition-colors shrink-0">
              <svg className="w-5 h-5 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-xl font-extrabold truncate">{t("plans.title", "Planuri copii")}</h1>
          </div>
          <p className="text-white/70 text-[13px] mt-1.5">
            {t("plans.subtitle", "Vezi ce plan are fiecare copil și treci-l pe unul cu plată.")}
          </p>
        </GameHero>

        <div className="px-4 pt-5 space-y-4">
          {status === "success" && (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
              {t("plans.success", "Plată reușită! Planul devine activ imediat după confirmare.")}
              <button className="ml-2 underline" onClick={() => setParams({})}>OK</button>
            </div>
          )}
          {status === "cancel" && (
            <div className="rounded-2xl bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700">
              {t("plans.canceled", "Plata a fost anulată. Poți relua oricând.")}
              <button className="ml-2 underline" onClick={() => setParams({})}>OK</button>
            </div>
          )}
          {notice && (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
              {notice}
              <button className="ml-2 underline" onClick={() => setNotice("")}>OK</button>
            </div>
          )}
          {error && (
            <div className="rounded-2xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">{error}</div>
          )}

          {loading ? (
            <GameCard className="p-4 text-sm text-slate-500">{t("common.loading", "Se încarcă...")}</GameCard>
          ) : children.length === 0 ? (
            <GameCard className="p-4 text-sm text-slate-600">
              {t("plans.no_children", "Nu ai copii înregistrați.")}
            </GameCard>
          ) : paidPlans.length === 0 ? (
            <GameCard className="p-4 text-sm text-slate-600">
              {t("plans.no_paid_plans", "Nu există planuri cu plată configurate momentan.")}
            </GameCard>
          ) : (
            children.map((child) => {
              const sub = child.subscription;
              const currentCode = sub?.plan_code || "free";
              const currentPlan = planByCode.get(currentCode);
              const onPaidActive = sub?.status === "active" && currentCode !== "free";
              const pendingPaid = sub?.status === "pending" && currentCode !== "free";
              const cancelAt = onPaidActive ? sub?.cancel_at || null : null;
              const scheduledCancel = !!cancelAt;
              const pick = choice[child.athlete_id] || { plan: paidPlans[0].code, billing: "month" as const };
              const picked = planByCode.get(pick.plan);
              const busy = busyChild === child.athlete_id;

              return (
                <GameCard key={child.athlete_id} className="p-5 space-y-4">
                  {/* Who + what they're on now */}
                  <div className="flex items-center gap-3">
                    {child.athlete?.profile_url ? (
                      <img
                        src={child.athlete.profile_url}
                        alt={child.athlete.full_name}
                        className="w-12 h-12 rounded-2xl object-cover shrink-0 ring-1 ring-slate-200"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 grid place-items-center text-white text-lg font-black shrink-0">
                        {child.athlete?.full_name?.charAt(0) || "?"}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-extrabold text-slate-800 truncate">{child.athlete?.full_name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {t("plans.current", "Plan curent")}: {currentPlan?.name || currentCode}
                        {pendingPaid && ` · ${t("plans.pending", "plată neîncheiată")}`}
                      </p>
                    </div>
                    {onPaidActive && (
                      <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold shrink-0">
                        {t("plans.active", "Activ")}
                      </span>
                    )}
                  </div>

                  {scheduledCancel && (
                    <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 space-y-2">
                      <p className="text-sm text-amber-800">
                        {t(
                          "plans.cancel_scheduled",
                          "Planul rămâne activ până pe {{date}}, apoi copilul trece pe Free.",
                          { date: new Date(cancelAt!).toLocaleDateString("ro-RO") }
                        )}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleResume(child.athlete_id)}
                        disabled={busy}
                        className="w-full py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold disabled:opacity-60"
                      >
                        {busy ? t("plans.processing", "Se procesează...") : t("plans.keep_plan", "Păstrează planul")}
                      </button>
                    </div>
                  )}

                  {onPaidActive && !scheduledCancel && (
                    <p className="text-xs text-slate-500">
                      {t(
                        "plans.change_hint",
                        "Poți trece pe alt plan oricând — plătești doar diferența pentru timpul rămas."
                      )}
                    </p>
                  )}

                  <>
                      {/* Plan picker.
                          Each card carries its OWN complete list of what the
                          child gets. It used to show a name, a price and one
                          line of description, which left the only real
                          question — what am I paying for — unanswered; and the
                          feature text in the database answers it with "tot ce e
                          în Start, plus...", a pointer to a card that is
                          scrolled off screen on a phone. `expandedFeatures`
                          resolves those into the full list. */}
                      <div className="space-y-2.5">
                        {paidPlans.map((p) => {
                          const active = pick.plan === p.code;
                          const isCurrent = onPaidActive && p.code === currentCode;
                          const yearly = pick.billing === "year" && p.price_cents_year != null;
                          const price = yearly ? (p.price_cents_year as number) : p.price_cents_month;
                          const perMonthIfYearly = yearly ? Math.round((p.price_cents_year as number) / 12) : null;
                          const features = expandedFeatures(p, plans);
                          return (
                            <button
                              type="button"
                              key={p.code}
                              onClick={() =>
                                setChoice((prev) => ({
                                  ...prev,
                                  [child.athlete_id]: { plan: p.code, billing: pick.billing },
                                }))
                              }
                              className={`w-full text-left px-4 py-3.5 rounded-2xl border-2 transition ${
                                active ? "border-indigo-500 bg-indigo-50/70" : "border-slate-200 bg-white"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <span className="min-w-0">
                                  <span className="block text-sm font-extrabold text-slate-800">
                                    {p.name}
                                    {isCurrent && (
                                      <span className="ml-2 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-bold align-middle">
                                        {t("plans.currentShort", "curent")}
                                      </span>
                                    )}
                                  </span>
                                  {p.description && (
                                    <span className="block mt-0.5 text-xs leading-relaxed text-slate-500">
                                      {p.description}
                                    </span>
                                  )}
                                </span>
                                <span className="text-right shrink-0">
                                  <span className="block text-base font-black leading-none text-slate-800 tabular-nums">
                                    {money(price, p.currency)}
                                  </span>
                                  <span className="block text-[11px] text-slate-400 mt-0.5">
                                    / {yearly ? t("common.year", "an") : t("common.month", "lună")}
                                  </span>
                                  {/* An annual price is only comparable to a
                                      monthly one once it is divided by twelve. */}
                                  {perMonthIfYearly !== null && (
                                    <span className="block text-[11px] font-semibold text-emerald-600 mt-0.5">
                                      ≈ {money(perMonthIfYearly, p.currency)}/{t("common.month", "lună")}
                                    </span>
                                  )}
                                </span>
                              </div>

                              {features.length > 0 && (
                                <ul className="pt-3 mt-3 space-y-1.5 border-t border-slate-200/70">
                                  {features.map((f, i) => (
                                    <li key={i} className="flex items-start gap-2 text-[13px] leading-relaxed text-slate-700">
                                      <svg
                                        className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                      </svg>
                                      <span>{f}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* The free plan, stated rather than implied. A parent
                          deciding whether to pay needs to see what staying put
                          actually means — and "3 vieți" is the line that makes
                          the paid pool legible as a difference. */}
                      {!onPaidActive && freePlan && (
                        <details className="px-4 py-3 border rounded-2xl border-slate-200 bg-slate-50">
                          <summary className="text-sm font-bold cursor-pointer text-slate-700">
                            {t("plans.free_includes", "Ce are acum, pe planul gratuit")}
                          </summary>
                          <ul className="mt-2 space-y-1.5">
                            {expandedFeatures(freePlan, plans).map((f, i) => (
                              <li key={i} className="flex items-start gap-2 text-[13px] leading-relaxed text-slate-600">
                                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}

                      {/* Billing period — only when the picked plan has an annual price */}
                      {picked?.price_cents_year != null && (
                        <div className="flex gap-2">
                          {(["month", "year"] as const).map((b) => (
                            <button
                              type="button"
                              key={b}
                              onClick={() =>
                                setChoice((prev) => ({
                                  ...prev,
                                  [child.athlete_id]: { plan: pick.plan, billing: b },
                                }))
                              }
                              className={`flex-1 py-2 text-sm font-semibold rounded-lg border ${
                                pick.billing === b
                                  ? "bg-indigo-600 text-white border-indigo-600"
                                  : "bg-white text-slate-600 border-slate-300"
                              }`}
                            >
                              {b === "month" ? t("plans.monthly", "Lunar") : t("plans.yearly", "Anual")}
                            </button>
                          ))}
                        </div>
                      )}

                      {!onPaidActive && projectedDiscount > 0 && (
                        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800">
                          🎉{" "}
                          {t(
                            "plans.discount_hint",
                            "Cu acest abonament ajungi la {{n}} plătite — primești {{pct}}% reducere la TOATE abonamentele copiilor tăi.",
                            { n: projectedPaid, pct: projectedDiscount }
                          )}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() =>
                          onPaidActive
                            ? handleChangePlan(child.athlete_id, child.athlete?.full_name || "")
                            : handleUpgrade(child.athlete_id)
                        }
                        disabled={busy || (onPaidActive && pick.plan === currentCode)}
                        className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-xl transition"
                      >
                        {busy
                          ? t("plans.processing", "Se procesează...")
                          : onPaidActive
                            ? t("plans.change", "Vezi costul schimbării")
                            : pendingPaid
                              ? t("plans.resume", "Continuă spre plată")
                              : t("plans.upgrade", "Treci pe plan cu plată")}
                      </button>

                      {onPaidActive && !scheduledCancel && (
                        <button
                          type="button"
                          onClick={() =>
                            askCancel(
                              child.athlete_id,
                              child.athlete?.full_name || "",
                              sub?.current_period_end || null
                            )
                          }
                          className="w-full text-center text-sm font-semibold text-slate-500 hover:text-rose-600 transition-colors"
                        >
                          {t("plans.switch_to_free", "Treci pe Free")}
                        </button>
                      )}
                  </>
                </GameCard>
              );
            })
          )}
        </div>
      </div>

      {/* Stopping a paid plan. No money moves here — the point of the dialog
          is that the parent knows the features run to the end of the period
          they already paid for, and only then drop to Free. */}
      {pendingCancel && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-4 pb-[calc(6rem+env(safe-area-inset-bottom,0px))] sm:pb-4">
          <div className="w-full max-w-md bg-white rounded-3xl p-5 space-y-4">
            <h2 className="text-lg font-black text-slate-800">
              {t("plans.cancel_title", "Treci copilul pe Free?")}
            </h2>
            <p className="text-sm text-slate-600">
              {pendingCancel.until
                ? t(
                    "plans.cancel_body_date",
                    "{{child}} păstrează toate funcțiile plătite până pe {{date}}. După acea dată contul devine Free.",
                    {
                      child: pendingCancel.childName,
                      date: new Date(pendingCancel.until).toLocaleDateString("ro-RO"),
                    }
                  )
                : t(
                    "plans.cancel_body",
                    "{{child}} păstrează toate funcțiile plătite până la finalul perioadei deja plătite. După aceea contul devine Free.",
                    { child: pendingCancel.childName }
                  )}
            </p>
            <p className="text-xs text-slate-500">
              {t("plans.cancel_no_refund", "Nu se face nicio rambursare și te poți răzgândi până la acea dată.")}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPendingCancel(null)}
                disabled={confirming}
                className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-600 font-semibold disabled:opacity-60"
              >
                {t("plans.keep_plan", "Păstrează planul")}
              </button>
              <button
                type="button"
                onClick={confirmCancel}
                disabled={confirming}
                className="flex-1 py-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold disabled:opacity-60"
              >
                {confirming ? t("plans.processing", "Se procesează...") : t("plans.cancel_confirm", "Treci pe Free")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proration confirmation. The parent sees exactly what Stripe worked
          out before anything is charged, and confirming reuses the same
          proration_date so the amount cannot drift between the two calls. */}
      {pendingChange && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-4 pb-[calc(6rem+env(safe-area-inset-bottom,0px))] sm:pb-4">
          <div className="w-full max-w-md bg-white rounded-3xl p-5 space-y-4">
            <h2 className="text-lg font-black text-slate-800">
              {t("plans.confirm_title", "Confirmi schimbarea planului?")}
            </h2>
            <p className="text-sm text-slate-600">
              {t("plans.confirm_body", "{{child}} trece pe planul {{plan}}.", {
                child: pendingChange.childName,
                plan: pendingChange.preview.new_plan_name,
              })}
            </p>

            <div className="rounded-2xl bg-slate-50 p-4 space-y-2">
              {pendingChange.preview.amount_due_cents > 0 ? (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{t("plans.due_now", "De plată acum")}</span>
                  <span className="text-lg font-black text-slate-800 tabular-nums">
                    {money(pendingChange.preview.amount_due_cents, pendingChange.preview.currency)}
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">{t("plans.due_now", "De plată acum")}</span>
                  <span className="text-lg font-black text-emerald-600 tabular-nums">
                    {money(0, pendingChange.preview.currency)}
                  </span>
                </div>
              )}
              {pendingChange.preview.credit_cents > 0 && (
                <p className="text-xs text-emerald-700">
                  {t("plans.credit_hint", "Primești {{amount}} credit, folosit automat la facturile următoare.", {
                    amount: money(pendingChange.preview.credit_cents, pendingChange.preview.currency),
                  })}
                </p>
              )}
              <p className="text-xs text-slate-500">
                {t(
                  "plans.proration_hint",
                  "Suma acoperă doar diferența pentru timpul rămas din perioada curentă."
                )}
              </p>
              {pendingChange.preview.next_renewal_at && (
                <p className="text-xs text-slate-500">
                  {t("plans.next_renewal", "Următoarea reînnoire")}:{" "}
                  {new Date(pendingChange.preview.next_renewal_at).toLocaleDateString("ro-RO")}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPendingChange(null)}
                disabled={confirming}
                className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-600 font-semibold disabled:opacity-60"
              >
                {t("common.cancel", "Anulează")}
              </button>
              <button
                type="button"
                onClick={confirmChange}
                disabled={confirming}
                className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold disabled:opacity-60"
              >
                {confirming ? t("plans.processing", "Se procesează...") : t("plans.confirm", "Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </MobileAppLayout>
  );
}
